import React, { useCallback, useMemo } from "react";
import {
  MenuItemProps,
  WSElement,
  WSTable,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { useHistory, useLocation } from "react-router-dom";

import DocumentMeta from "./DocumentMeta";
import { SidePanelSection } from "../../../components/SidePanel";

const DocumentTitle: React.FC<{ data: DocumentMeta }> = ({ data }) => (
  <WSText>
    {data.title}
    <WSText inline color="grey500" ml="XS">
      {data.year}
    </WSText>
  </WSText>
);

const DocumentAmount: React.FC<{ amount: number }> = ({ amount }) => (
  <WSText formatMoney color="grey500" mt="XS">
    {amount}
  </WSText>
);

export interface DocumentsProps {
  documents: DocumentMeta[];
  renderBadge?: (data: WSTableItem<DocumentMeta>) => React.ReactNode;
  showAmounts?: boolean;
  testid?: string;
}

const DocumentsSection: React.FC<DocumentsProps> = ({
  documents,
  showAmounts,
  renderBadge,
  testid
}) => {
  const history = useHistory();
  const location = useLocation();

  const tableData = useMemo(
    () => documents.map(doc => ({ id: doc.year, data: doc })),
    []
  );

  const renderDocInfo = ({ data }: WSTableItem<DocumentMeta>) => (
    <WSElement ml="-S">
      <DocumentTitle data={data} />
      {showAmounts && <DocumentAmount amount={data.amount} />}
    </WSElement>
  );

  const columnsConfig = useMemo(() => {
    const badgeColumnConfig = renderBadge
      ? [
          {
            config: {
              gridTemplateSizeMin: "auto",
              gridTemplateSizeMax: "auto"
            },
            renderFunction: renderBadge
          }
        ]
      : [];

    return [
      {
        config: {
          gridTemplateSizeMin: "auto",
          gridTemplateSizeMax: "1fr"
        },
        renderFunction: renderDocInfo
      },
      ...badgeColumnConfig
    ];
  }, [renderBadge, showAmounts]);

  const getActions = useCallback(
    ({ data: docMeta }: { data: DocumentMeta }): MenuItemProps[] => [
      {
        label: docMeta.isAcceptedByIrs ? "View PDF" : "View",
        icon: "search",
        onClick: docMeta.isAcceptedByIrs
          ? () => {}
          : () => {
              history.push(`${location.pathname}/tax-form-draft`);
            }
      },
      {
        label: "Download",
        icon: "download",
        onClick: () => {}
      }
    ],
    []
  );

  return (
    <SidePanelSection data-testid={testid}>
      <WSText.Heading3 mb="S">Documents</WSText.Heading3>
      <WSTable
        showHeader={false}
        columns={columnsConfig}
        rowActions={getActions}
        tableData={tableData}
      />
    </SidePanelSection>
  );
};

export default DocumentsSection;
